/**
 * useUIMode — Hook para gestionar el modo de UI (Simple/Avanzado) en Web.
 * Mirrors mobile `useSimpleMode`. Persiste via `PATCH /auth/me/preferences`
 * y expone estado + setter globalmente.
 *
 * Uso:
 *   const { mode, isSimple, isAdvanced, setMode, toggle } = useUIMode();
 */
import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { logUXEvent } from "../lib/uxTelemetry";

// Cache global de módulos (evita fetch por hook mount)
let CACHED_MODE = null;
const LISTENERS = new Set();

function notifyAll(mode) {
  CACHED_MODE = mode;
  LISTENERS.forEach((fn) => {
    try { fn(mode); } catch { /* listener may unmount mid-notify */ }
  });
}

async function fetchModeFromServer() {
  try {
    const me = await api.request("/auth/me");
    return me?.preferences?.ui_mode || "simple";
  } catch {
    return "simple";
  }
}

export function useUIMode() {
  const [mode, setModeState] = useState(CACHED_MODE || "simple");
  const [loading, setLoading] = useState(CACHED_MODE === null);

  useEffect(() => {
    if (CACHED_MODE !== null) {
      setModeState(CACHED_MODE);
      setLoading(false);
    } else {
      fetchModeFromServer().then((m) => {
        notifyAll(m);
        setLoading(false);
      });
    }
    const listener = (m) => setModeState(m);
    LISTENERS.add(listener);
    return () => { LISTENERS.delete(listener); };
  }, []);

  const setMode = useCallback(async (newMode, source = "unknown") => {
    const normalized = newMode === "advanced" ? "advanced" : "simple";
    const previous = CACHED_MODE || "simple";
    // Telemetría (fire-and-forget) — solo si cambia realmente
    if (previous !== normalized) {
      logUXEvent("ui_mode_change", {
        from: previous,
        to: normalized,
        source,
      });
    }
    notifyAll(normalized); // optimistic
    try {
      await api.request("/auth/me/preferences", {
        method: "PATCH",
        body: JSON.stringify({ ui_mode: normalized }),
      });
    } catch {
      // Silent revert not needed — cached is source of truth for UI feel
    }
  }, []);

  const toggle = useCallback(() => {
    setMode(mode === "simple" ? "advanced" : "simple", "toggle-fn");
  }, [mode, setMode]);

  return {
    mode,
    isSimple: mode === "simple",
    isAdvanced: mode === "advanced",
    setMode,
    toggle,
    loading,
  };
}
